import { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link } from 'react-router-dom';
import { t } from 'i18next';

import { ConfirmationModal } from '@components';
import { useDeleteActivityLogMutation } from '@common/cache';

import { IActivityLogsTableEntry } from '../hooks';
import { AddActivityLog } from './AddActivityLog';

interface IActivityLogsRowActionsProps {
  row: IActivityLogsTableEntry;
  disabled: boolean;
}

export const ActivityLogsRowActions = ({
  row,
  disabled,
}: IActivityLogsRowActionsProps) => {
  const deleteMutation = useDeleteActivityLogMutation(row.id);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const handleDelete = async () => {
    await deleteMutation.mutateAsync();
    setIsConfirmDeleteOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        justifyContent: 'flex-end',
        height: '100%',
      }}
    >
      {row.patient_id ? (
        <Link
          to={`/patient/${row.patient_id}`}
          onClick={e => e.stopPropagation()}
        >
          <IconButton>
            <OpenInNew sx={{ width: 24, height: 24 }} />
          </IconButton>
        </Link>
      ) : (
        <>
          <AddActivityLog
            variant='edit'
            activity_id={row.id}
            disabled={disabled || !row.editable}
          />
          <IconButton
            disabled={disabled || !row.editable}
            onClick={e => {
              e.stopPropagation();
              setIsConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon
              color={disabled || !row.editable ? 'disabled' : 'primary'}
            />
          </IconButton>
        </>
      )}
      <ConfirmationModal
        title={t('activityLog.deleteModal.title')}
        message={t('activityLog.deleteModal.message')}
        onConfirm={handleDelete}
        onClose={() => setIsConfirmDeleteOpen(false)}
        isOpen={isConfirmDeleteOpen}
        isLoading={deleteMutation.isPending}
        confirmText={t('activityLog.deleteModal.confirm')}
      />
    </Box>
  );
};
